"use client";

import React, { useState, useMemo, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import Pagination from "../../common/Pagination";

const PER_PAGE = 12;

// ─── Specialty config ────────────────────────────────────────────────────────
const SPECIALTY_CONFIG = {
  "General Surgery": {
    heading: "GENERAL SURGERY SPECIALISTS",
    detailPage: "/general-surgery-detailpage",
  },
  "Cardio-Thoracic": {
    heading: "CARDIO-THORACIC SURGERY SPECIALISTS",
    detailPage: "/cardio-thoracic-surgery-detailpage",
  },
  "Robotic": {
    heading: "ROBOTIC SURGERY SPECIALISTS",
    detailPage: "/robotic-surgery-detailpage",
  },
  "Orthopedic": {
    heading: "ORTHOPEDIC SURGERY SPECIALISTS",
    detailPage: "/orthopedic-surgery-detailpage",
  },
  "Obs-Gyn": {
    heading: "OBSTETRICS & GYNECOLOGY SURGERY SPECIALISTS",
    detailPage: "/obs-gyn-surgery-detailpage",
  },
  "Neuro-Spinal": {
    heading: "NEURO-SPINAL SURGERY SPECIALISTS",
    detailPage: "/neuro-spinal-surgery-detailpage",
  },
};

const DEFAULT_SPECIALTY = "General Surgery";

// ─── Sorting utility ─────────────────────────────────────────────────────────
function getLastName(fullName) {
  if (!fullName) return "";
  let name = fullName.replace(/^Dr\.\s*/i, "").trim();
  const commaIdx = name.indexOf(",");
  if (commaIdx !== -1) name = name.substring(0, commaIdx).trim();
  const parts = name.trim().split(/\s+/);
  let lastName = parts[parts.length - 1] || "";
  const suffixes = ["jr", "jr.", "sr", "sr.", "ii", "iii", "iv", "v"];
  if (suffixes.includes(lastName.toLowerCase()) && parts.length > 1) {
    lastName = parts[parts.length - 2];
  }
  return lastName;
}

function sortByLastName(data) {
  return [...data].sort((a, b) => {
    const priorityA = a.priority || 999;
    const priorityB = b.priority || 999;
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    const nameA = a.surgeonTxt1 || a.name || "";
    const nameB = b.surgeonTxt1 || b.name || "";
    const lastA = getLastName(nameA).toLowerCase();
    const lastB = getLastName(nameB).toLowerCase();
    if (lastA < lastB) return -1;
    if (lastA > lastB) return 1;
    return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
  });
}

// ─── Component ───────────────────────────────────────────────────────────────
const SurgeonDirectory = ({ allSurgeons, surgeonIcon }) => {
  const searchParams = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [activeSpecialty, setActiveSpecialty] = useState(DEFAULT_SPECIALTY);

  // Sync specialty from URL param on mount and param change
  useEffect(() => {
    const paramSpecialty = searchParams.get("specialty");
    if (paramSpecialty && SPECIALTY_CONFIG[paramSpecialty]) {
      setActiveSpecialty(paramSpecialty);
    } else {
      setActiveSpecialty(DEFAULT_SPECIALTY);
    }
    setCurrentPage(1);
  }, [searchParams]);

  const config = SPECIALTY_CONFIG[activeSpecialty] || SPECIALTY_CONFIG[DEFAULT_SPECIALTY];

  // Filter by specialtyType
  const filtered = useMemo(() => {
    return allSurgeons.filter((s) => s.specialtyType === activeSpecialty);
  }, [allSurgeons, activeSpecialty]);

  // Sort alphabetically by last name
  const sorted = useMemo(() => sortByLastName(filtered), [filtered]);

  // Paginate
  const totalPages = Math.ceil(sorted.length / PER_PAGE);
  const paginated = useMemo(() => {
    return sorted.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
  }, [sorted, currentPage]);

  // Reset page when specialty changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSpecialty]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const el = document.getElementById("directory-top");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="service-page" className="speciality-page">
      <div id="directory-top" style={{ paddingTop: "20px" }}></div>
      <div className="service-support">
        <div className="container">

          {/* ── Dynamic Subheading ── */}
          <div className="directory-subheading">
            <h2 className="l-h-1 color-blue f-w-300 font-barlow t-t-u">
              {config.heading}
            </h2>
          </div>

          {/* ── Surgeon Cards Grid ── */}
          <div className="md-team">
            {paginated.length === 0 ? (
              <div className="no-results">
                <p>No surgeons found for this specialty.</p>
              </div>
            ) : (
              paginated.map((item, idx) => {
                const name = item.surgeonTxt1 || item.name || "";
                const title = item.surgeonTxt2 || "Universal Surgical Assistant";
                const address = item.surgeonTxt3 || "Texas";
                const specialty = item.surgeonTxt4 || "";
                const phoneText = item.surgeonTxt5 || "Call (713) 842-9015";
                const img = item.surgeonImg || null;
                const icon = surgeonIcon || item.surgeonIcon || "/images/services/s-logo.png";

                return (
                  <Link
                    key={`${name}-${idx}`}
                    href={{
                      pathname: config.detailPage,
                      query: { slug: name }, hash: 'surgeonD',
                    }}
                    passHref
                  >
                    <div className="flex ssup-box surgeon-card" style={{ cursor: "pointer", marginBottom: "30px" }}>
                      <div className="flex center-center ssup-content">
                        <div className="card">
                          <div className="logo-section">
                            <img src={icon} alt="Logo" />
                          </div>
                          <div className="info-section">
                            <h1 className="font-barlow">{name}</h1>
                            <h2 className="font-barlow">{title}</h2>
                            <p className="address font-barlow">{address}</p>
                            <p className="specialty">{specialty}</p>
                            <span className="call-btn">{phoneText}</span>
                          </div>
                        </div>
                      </div>

                      <div className="img img-box md-image" style={{ width: "226px", height: "270px" }}>
                        {img ? (
                          <div style={{ position: "relative", width: "100%", height: "100%" }}>
                            <Image
                              className="b-r-5"
                              src={img}
                              alt={name}
                              fill
                              style={{ objectFit: "cover", objectPosition: "top" }}
                            />
                          </div>
                        ) : (
                          <div className="placeholder-img"></div>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          {/* ── Pagination ── */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={sorted.length}
            startIdx={sorted.length > 0 ? (currentPage - 1) * PER_PAGE + 1 : 0}
            endIdx={Math.min(currentPage * PER_PAGE, sorted.length)}
          />
        </div>
      </div>

      <style jsx>{`
        .directory-subheading {
          padding: 30px 0 20px 0;
          border-bottom: 2px solid #e2e8f0;
          margin-bottom: 30px;
        }
        .directory-subheading h2 {
          margin: 0;
          font-size: clamp(1.1rem, 2.5vw, 1.6rem);
          letter-spacing: 1px;
        }
        .surgeon-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .surgeon-card:hover {
          transform: translateY(-5px);
        }
        .placeholder-img {
          width: 100%;
          height: 100%;
          background: #f3f4f6;
          border-radius: 5px;
        }
        .no-results {
          text-align: center;
          padding: 60px 0;
          color: #64748b;
          font-size: 1.1rem;
        }
      `}</style>
    </section>
  );
};

export default SurgeonDirectory;
